



















































import Enumerable from "linq";
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { LightTable, LightRow, Header, Row, Cell } from "../../../../domain/system/table";
import { SelectionModeKinds } from "../../../../domain/document";
import ScrollableTableRow from "./ScrollableTableRow.vue";
import LightScrollableTableRow from "./LightScrollableTableRow.vue";
import { UserDefinitionTextFilterKinds } from "./UserDefinitionTextFilterKinds";
import { UserDefinitionFilterPatternKinds } from "./UserDefinitionFilterPatternKinds";
import { StringUtility } from "../../../../domain/system";

@Component({
  components: {
    ScrollableTableRow,
    LightScrollableTableRow,
  },
})
export default class LightScrollableTable extends Mixins() {
  data(): any {
    return {
      currentPage: 1,
      displayRows: [] as LightRow[],
      filteredRowCount: 0,
    };
  }
  currentPage: number;
  displayRows: LightRow[];
  filteredRowCount: number;

  @Prop()
  table: LightTable;

  @Prop({ default: SelectionModeKinds.Row })
  selectionMode: SelectionModeKinds;

  @Prop({ default: true })
  enableMultiSelect: boolean;

  @Prop({ default: 100 })
  rowCountPerPage: number;

  @Prop({ default: false })
  enableFilter: boolean;

  @Prop({ default: UserDefinitionTextFilterKinds.Contains })
  textFilter: UserDefinitionTextFilterKinds;

  @Prop({ default: UserDefinitionFilterPatternKinds.And })
  filterPatrern: UserDefinitionFilterPatternKinds;

  @Prop({ default: true })
  enableHalfDistinction: boolean;

  mounted(): any {
    this.adjustWidth();
    this.updateRows();
  }

  /**
   * element-uiのテーブルのD&Dでの横幅調整を使いたいが、
   * ヘッダーのスタイル指定がどうにもうまくできないので、横幅指定を合わせこむことでなんとか見られる表示にごまかしている
   */
  adjustWidth(): void {
    for (const header of this.table.headers) {
      if (!header.title) { continue; }
      const minWidth = header.title.length * 18;
      header.width = (!!header.width && header.width > minWidth) ? header.width : minWidth;
    }
  }

  get filterRow(): Row {
    const cells: Cell[] = [];
    for (const header of this.table.headers) {
      const cell = new Cell(header.filterText);
      cells.push(cell);
    }
    return new Row(cells);
  }

  get isSinglePage(): boolean {
    return (!this.rowCountPerPage || (this.rowCountPerPage <= 0) || (this.rowCountPerPage >= this.filteredRowCount));
  }

  @Watch("currentPage")
  updateRows(): void {
    const filteredRows = this.getFilteredRows();
    this.filteredRowCount = filteredRows.length;
    this.displayRows = this.getPagedRows(filteredRows);
    this.resetScrollPosition();
  }

  @Watch("table")
  tableChanged(): void {
    this.adjustWidth();
    this.updateRows();
  }

  @Watch("textFilter")
  textFilterChanged(): void {
    this.updateRows();
  }

  @Watch("filterPatrern")
  filterPatrernChanged(): void {
    this.updateRows();
  }

  @Watch("enableHalfDistinction")
  enableHalfDistinctionChanged(): void {
    this.updateRows();
  }

  getPagedRows(rows: LightRow[]): LightRow[] {
    if (this.isSinglePage) { return rows; }
    return rows.slice(this.rowCountPerPage * (this.currentPage - 1), this.rowCountPerPage * this.currentPage);
  }

  getFilteredRows(): LightRow[] {
    if (this.isFilterEmpty) { return this.table.rows; }
    const rows: LightRow[] = [];
    for (const row of this.table.rows) {
      if (!this.isMatch(row)) { continue; }
      rows.push(row);
    }
    return rows;
  }

  get isFilterEmpty(): boolean {
    if (!this.enableFilter) { return true; }
    for (const header of this.table.headers) {
      if (!!header.filterText || header.filterItems.length > 0) { return false; }
    }
    return true;
  }

  isMatch(row: LightRow): boolean {
    if (this.filterPatrern === UserDefinitionFilterPatternKinds.And) {
      // すべての条件が一致する場合にtrue
      for (let index = 0; index < this.table.headers.length; ++index) {
        const header = this.table.headers[index];
        if (header.filterItems.length > 0) {
          if (!this.isItemsMatch(row.values[index], header.filterItems)) { return false; }
        }
        if (header.filterText) {
          if (!this.isTextMatch(row.values[index], header.filterText)) { return false; }
        }
      }
      return true;
    }
    if (this.filterPatrern === UserDefinitionFilterPatternKinds.Or) {
      // どれか一致するCellがある場合にtrue
      for (let index = 0; index < this.table.headers.length; ++index) {
        const header = this.table.headers[index];
        if (header.filterItems.length > 0) {
          if (this.isItemsMatch(row.values[index], header.filterItems)) { return true; }
        }
        if (header.filterText) {
          if (this.isTextMatch(row.values[index], header.filterText)) { return true; }
        }
      }
      return false;
    }
    return true;
  }

  isTextMatch(cellText: string, filterText: string): boolean {
    if (!filterText) { return true; }
    if (!cellText) { return false; }
    const convertedCellText = this.enableHalfDistinction
      ? cellText.toLowerCase()
      : StringUtility.convertFromFullToHalf(cellText.toLowerCase());
    const convertedFilterText = this.enableHalfDistinction
      ? filterText.toLowerCase()
      : StringUtility.convertFromFullToHalf(filterText.toLowerCase());
    const index = convertedCellText.indexOf(convertedFilterText);
    if (this.textFilter === UserDefinitionTextFilterKinds.Contains) {
      return (index !== -1);
    }
    if (this.textFilter === UserDefinitionTextFilterKinds.StartsWith) {
      return (index === 0);
    }
    return true;
  }

  isItemsMatch(cellText: string, filterItems: string[]): boolean {
    return Enumerable.from(filterItems)
      .contains(cellText);
  }

  get headers(): Header[] {
    return this.table.headers;
  }

  get visibleHeaders(): Header[] {
    return this.headers.filter(h => h.isVisible);
  }

  get columnsCells(): string[][] {
    const ret: string[][] = [];
    Enumerable.from(this.table.headers).forEach((h, index) => {
      ret.push(Enumerable.from(this.table.rows)
        .select(s => s.values[index])
        .distinct()
        .toArray());
    });
    return ret;
  }

  onScroll(): void {
    const headerContainer = this.$refs.headerContainer as HTMLDivElement;
    const rowsContainer = this.$refs.rowsContainer as HTMLDivElement;
    if (headerContainer.scrollLeft === rowsContainer.scrollLeft) { return; }
    headerContainer.scrollLeft = rowsContainer.scrollLeft;
  }

  onClicked(row: LightRow, cellIndex: number, isWithCtrl: boolean): void {
    if (!this.enableMultiSelect) {
      this.table.clearSelection();
    }
    if (this.selectionMode === SelectionModeKinds.Cell) {
      // セル選択
      row.selectCell(cellIndex, !row.isCellSelected(cellIndex));
    } else {
      // 全行選択
      if (!isWithCtrl) {
        this.table.clearSelection();
      }
      row.selectRow();
    }
    this.$emit("click");
  }

  onDoubleClicked(row: LightRow, cellIndex: number): void {
    this.onClicked(row, cellIndex, false);
    this.$emit("doubleClick");
  }

  onFilterTextChanged(index: number, text: string): void {
    this.currentPage = 1;
    this.table.headers[index].filterText = text;
    this.updateRows();
  }

  onFilterItemsChanged(index: number, texts: string[]): void {
    this.currentPage = 1;
    this.table.headers[index].filterItems = texts;
    this.updateRows();
  }

  onEnterKeyDown(index: number): void {
    if (this.filteredRowCount !== 1) {
      return;
    }
    this.onClicked(this.displayRows[0], 0, false);
    this.$emit("filteredSingleItem");
  }

  onHeaderDragEnd(newWidth: number, oldWidth: number, column: any, event: any): void {
    const index = parseInt(column.columnKey, 10);
    this.visibleHeaders[index].width = newWidth;
  }

  headerCellStyleProperties(): any {
    return {
      fontSize: "12px",
      fontWeight: "normal",
    };
  }

  resetScrollPosition(): void {
    // ページ遷移が起こった時にスクロールを初期位置に戻す
    const container = this.$refs.rowsContainer as HTMLDivElement;
    if (!container) { return; }
    container.scrollTop = 0;
    container.scrollLeft = 0;
  }
}
